import { IconButton } from '@vrplatform/components';
import {
  useBilling,
  useDashboard,
  useTeam,
  useTeamRole,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { day, type Maybe } from '@vrplatform/utils';
import {
  AppShell,
  Box,
  Group,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  rem,
} from '@mantine/core';
import type { JSX } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useNavbarExpanded } from '../../hooks';
import {
  NavbarAccountingSwitch,
  NavbarApiSwitcher,
  NavbarFeaturesButton,
  NavbarIssueReportButton,
  NavbarNotificationsIconButton,
  NavbarSupportButton,
  NavbarTeamSwitch,
  NavbarTrialCard,
  UserSettingsPopover,
} from './_components';
import { NavbarBooksClosedDate } from './_components/NavbarBooksClosedDate';
import { useAuth } from '@clerk/clerk-react';
import { VRP_TENANT_ID } from '@vrplatform/graphql';

export type NavigationComponent = ({
  navbarIsExpanded,
}: {
  navbarIsExpanded: boolean;
}) => JSX.Element;

export type NavbarBaseProps = {
  navigation: NavigationComponent;
};

const isLocalhost =
  window.location.host.includes('localhost') ||
  window.location.host.includes('127.0.0.1');

export const Navbar = ({ navigation: Navigation }: NavbarBaseProps) => {
  const { isDesktopExpanded: isExpanded } = useNavbarExpanded();
  const { actor } = useAuth();
  const [dashboard] = useDashboard();
  const [{ isOnboarding, isGL: GL }] = useTeam();
  const { isVrpAdmin } = useTeamRole();

  const actorPublicMetadata = actor?.publicMetadata as Maybe<{
    allowed_ids: string;
    user_id: string;
    role: 'admin' | 'user' | 'owner';
  }>;

  const showApiSwitch =
    isVrpAdmin ||
    isLocalhost ||
    actorPublicMetadata?.role === 'admin' ||
    actorPublicMetadata?.allowed_ids.includes(VRP_TENANT_ID);

  const showFeaturesButton = dashboard !== 'owner' && !isOnboarding;

  return (
    <>
      <NavbarHeader />
      <AppShell.Section
        grow
        component={ScrollArea}
        px="sm"
        mt={isExpanded ? 'md' : undefined}
        sx={{
          width: isExpanded ? 280 : 80,
          height: '100%',
          paddingTop: !isExpanded ? 'sm' : undefined,
          gap: '1rem',
          '&, & > div, & > div > div': {
            height: '100%',
          },
        }}
      >
        <Stack
          h="100%"
          align={isExpanded ? undefined : 'center'}
          pt={!isExpanded ? 'sm' : undefined}
        >
          {!GL && (
            <>
              <NavbarTrialCard />
              <CancellationCard />
            </>
          )}
          {dashboard === 'propertyManager' && <NavbarAccountingSwitch />}
          {Navigation && <Navigation navbarIsExpanded={!!isExpanded} />}
        </Stack>
      </AppShell.Section>

      {GL ? (
        <>
          <Box
            mb="xs"
            sx={(theme) => ({
              display: 'flex',
              flexDirection: isExpanded ? 'row' : 'column',
              alignItems: isExpanded ? 'flex-start' : 'center',
              justifyContent: 'flex-start',
              paddingInline: 14,
              gap: isExpanded ? theme.spacing.xs : 0,
            })}
          >
            <NavbarExpandButton />
            <NavbarSupportButton />
            {isVrpAdmin && !isLocalhost && <NavbarIssueReportButton />}
            {showApiSwitch && <NavbarApiSwitcher />}
            {showFeaturesButton && <NavbarFeaturesButton />}
          </Box>
          <Box
            mt="auto"
            mb="sm"
            px="xs"
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing.xs,
            })}
          >
            <NavbarBooksClosedDate />
            <NavbarTrialCard />
            <CancellationCard />
          </Box>
        </>
      ) : (
        <>
          <NavbarSupportButton />
          {isVrpAdmin && !isLocalhost && <NavbarIssueReportButton />}
          {showApiSwitch && <NavbarApiSwitcher />}
          {showFeaturesButton && <NavbarFeaturesButton />}
        </>
      )}

      <AppShell.Section
        px="xs"
        pb="xs"
        pt={4}
        sx={{
          borderTop: '1px solid #C6C6C630',
          width: isExpanded ? 280 : 80,
        }}
      >
        <UserSettingsPopover isOwnerDashboard={dashboard === 'owner'} />
      </AppShell.Section>
    </>
  );
};

const NavbarHeader = () => {
  const [{ isGL: GL }] = useTeam();
  const { isDesktopExpanded: isExpanded } = useNavbarExpanded();

  const location = useLocation();

  const isSettingsView = location.pathname.startsWith('/settings');

  const goto = useNavigate();

  return (
    <AppShell.Section
      component={Group}
      wrap="nowrap"
      px="md"
      pt="md"
      gap={!isExpanded ? 0 : 'xs'}
      justify={isExpanded ? 'apart' : 'center'}
      sx={{
        width: isExpanded ? 280 : 80,
        height: '100%',
        flex: 0,
        paddingBottom: !isExpanded ? 10 : undefined,
        borderBottom: !isExpanded ? '1px solid #C6C6C630' : undefined,
        flexDirection: isExpanded ? undefined : 'column',
      }}
    >
      {isSettingsView ? (
        <Group gap={'xs'} flex={1}>
          <IconButton
            icon="ArrowLeftIcon"
            onClick={() => goto('/')}
            mb={!isExpanded ? 'xs' : undefined}
            color={(theme) => theme.colors.neutral[3]}
          />
          {isExpanded && (
            <Text component="span" size="lg" color="white">
              Settings
            </Text>
          )}
        </Group>
      ) : (
        <NavbarTeamSwitch />
      )}

      {GL ? (
        !isSettingsView && <NavbarNotificationsIconButton />
      ) : (
        <NavbarExpandButton />
      )}
    </AppShell.Section>
  );
};

// const SettingsButton = () => {
//   const goto = useNavigate();

//   const { isMobile } = useNavbarExpanded();

//   const isSettingsView = location.pathname.startsWith('/settings');

//   if (isMobile) return null;

//   return (
//     <IconButton
//       icon={isSettingsView ? 'ArrowLeftIcon' : 'GearIcon'}
//       onClick={() => goto(isSettingsView ? '/' : '/settings')}
//       iconSize={18}
//       tooltip="Settings"
//       color={(theme) => theme.colors.neutral[3]}
//     />
//   );
// };

const NavbarExpandButton = () => {
  const { setIsDesktopExpanded, isMobile, canExpand } = useNavbarExpanded();
  const [{ isGL: GL }] = useTeam();

  const toggle = () => setIsDesktopExpanded?.((e) => !e);

  if (isMobile || !canExpand) return null;

  return (
    <IconButton
      icon="Grid06Icon"
      onClick={toggle}
      tooltip="Toggle navigation"
      iconSize={GL ? 18 : 16}
      color={GL ? (theme) => theme.colors.neutral[3] : '#ffffff90'}
    />
  );
};

const CancellationCard = () => {
  const {
    subscription: {
      status: billingStatus,
      cancelledAt: subscriptionCancelledAt,
    },
  } = useBilling();
  const [dashboard] = useDashboard();
  const { isDesktopExpanded } = useNavbarExpanded();

  const location = useLocation();
  const { isTeamAdmin } = useTeamRole();
  const goto = useNavigate();

  const isUpcomingCancellation =
    billingStatus === 'active' &&
    !!subscriptionCancelledAt &&
    day(subscriptionCancelledAt).isAfter(day());

  const isCancelled = billingStatus === 'cancelled';

  const showCancellationCard =
    dashboard !== 'owner' && (isCancelled || isUpcomingCancellation);

  if (showCancellationCard) {
    const showHover = isTeamAdmin && !location.pathname.startsWith('/settings');

    const title = isCancelled
      ? 'Subscription cancelled'
      : 'Upcoming cancellation';

    return (
      <Tooltip label={title} disabled={isDesktopExpanded}>
        <Box
          p={rem(8)}
          title={title}
          onClick={() => {
            if (showHover) goto('/settings/team/billing');
          }}
          mx={isDesktopExpanded ? undefined : 'auto'}
          data-testid="upcoming-cancellation-card"
          sx={(theme) => {
            if (isCancelled)
              return {
                backgroundColor: '#ffffff10',
                borderRadius: theme.radius.md,
                border: '2px solid transparent',
                ':hover': showHover
                  ? {
                      cursor: 'pointer',
                      backgroundColor: '#ffffff20',
                      border: '2px solid transparent',
                    }
                  : undefined,
              };

            return {
              border: `1px solid ${theme.colors.yellow[7]}30`,
              backgroundColor: theme.colors.yellow[7] + 20,
              borderRadius: theme.radius.md,
              ':hover': showHover
                ? {
                    cursor: 'pointer',
                    backgroundColor: theme.colors.yellow[7] + 40,
                    border: `1px solid ${theme.colors.yellow[7]}50`,
                  }
                : undefined,
            };
          }}
        >
          <Group wrap="nowrap" gap="xs">
            <Icon
              icon="AlertTriangleIcon"
              color={isCancelled ? 'white' : (theme) => theme.colors.yellow[7]}
              size={16}
            />
            {isDesktopExpanded && (
              <Text size="sm" c={isCancelled ? 'white' : 'yellow'}>
                {title}
              </Text>
            )}
          </Group>
          {isDesktopExpanded && isUpcomingCancellation && (
            <Text size="xs" mt={rem(5)}>
              Your subscription will cancel on{' '}
              {day(subscriptionCancelledAt).format('LL')}.
            </Text>
          )}
        </Box>
      </Tooltip>
    );
  }

  return null;
};
